<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { RsCheckbox, RsLoading } from "@/components";

import JobDetails from "./JobDetails.vue";

import { useBrandStore } from "@/stores";
import { jobsService } from "@/services";

import type { Job } from "@/models/Job";

const includeClientLevel = ref(false);

const isRefreshing = ref(false);
const state = ref<{ status: "loading" } | { status: "error" } | { status: "done"; jobs: Job[] }>({ status: "loading" });

const selectedBrand = computed(() => useBrandStore().selectedBrand);
const selectedClient = computed(() => useBrandStore().selectedClient);

const delayTimeout = computed(() =>
  state.value.status === "done" &&
  state.value.jobs.some((j) => j.status === "queued" || j.status === "processing" || j.status === "deleting")
    ? 3_000
    : 10_000,
);

async function loadJobs(silent = false) {
  if (!silent) state.value = { status: "loading" };
  else isRefreshing.value = true;

  try {
    const jobs = await jobsService.GetJobs(selectedClient.value!, selectedBrand.value!, includeClientLevel.value);
    state.value = { status: "done", jobs };
  } catch (e) {
    console.error(e);
    state.value = { status: "error" };
  } finally {
    isRefreshing.value = false;
  }
}

const refreshJobs = () => loadJobs(true);

watch(
  [includeClientLevel, selectedBrand, selectedClient],
  (_new, _old, onCleanup) => {
    let timerId: NodeJS.Timeout;

    const onTimeout = () => void loadJobs(true).then(() => (timerId = setTimeout(onTimeout, delayTimeout.value)));
    loadJobs().then(() => (timerId = setTimeout(onTimeout, delayTimeout.value)));

    onCleanup(() => clearTimeout(timerId));
  },
  { immediate: true },
);
</script>
<template>
  <div class="tw-size-full tw-overflow-y-auto tw-p-4">
    <div class="tw-flex tw-flex-col tw-gap-4 tw-bg-white tw-p-4">
      <h1 class="tw-sr-only">Jobs</h1>
      <div class="tw-flex tw-items-center tw-gap-4">
        <RsCheckbox v-model="includeClientLevel">Include client-level jobs</RsCheckbox>
        <div class="tw-flex-auto" />
        <span v-show="isRefreshing" aria-live="polite" aria-label="Refreshing jobs">
          <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
          Refreshing...
        </span>
        <button
          type="button"
          :disabled="state.status === 'loading' || isRefreshing"
          class="tw-btn-md-green tw-group"
          @click="refreshJobs"
        >
          <i aria-hidden="true" class="fas fa-arrow-rotate-right group-disabled:tw-animate-spin" />
          Refresh
        </button>
      </div>
      <div v-if="state.status === 'loading'" class="tw-relative tw-h-32">
        <RsLoading id="jobs-loading" message="Loading your jobs..." />
      </div>
      <p
        v-else-if="state.status === 'error'"
        class="tw-grid tw-grow tw-place-items-center tw-p-4 tw-text-sm tw-text-red-800"
      >
        Failed to load jobs. Please try again.
      </p>
      <p
        v-else-if="state.jobs.length === 0"
        class="tw-grid tw-grow tw-place-items-center tw-p-4 tw-text-sm tw-text-gray-800"
      >
        No jobs available.
      </p>
      <template v-else>
        <div class="tw-min-h-0 tw-w-full tw-overflow-hidden tw-rounded-lg tw-border">
          <div class="tw-overflow-auto">
            <table class="tw-w-full tw-self-start">
              <thead>
                <tr class="z-1 tw-h-7 tw-divide-x tw-border-b [&>*]:tw-bg-gray-100 [&>*]:tw-p-2">
                  <th
                    v-for="col in ['Created', 'File Type', 'Status', 'Extension', 'Expires', 'Parameters', 'Actions']"
                    :key="col"
                    scope="col"
                    class="tw-min-w-24 tw-text-left"
                  >
                    {{ col }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <JobDetails
                  v-for="(job, index) in state.jobs"
                  :key="job.jobId"
                  :job="job"
                  :class="index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-gray-50'"
                  @refresh="refreshJobs"
                />
              </tbody>
            </table>
          </div>
        </div>
        <p class="tw-font-medium tw-tracking-wide tw-text-gray-600">Jobs expire 7 days after completion.</p>
      </template>
    </div>
  </div>
</template>
